import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const SpotifyPlayer = ({ onReady, onPlayerStateChanged }) => {
  const [player, setPlayer] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [spotifyProfile, setSpotifyProfile] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  // Check Spotify connection status
  useEffect(() => {
    checkSpotifyConnection();
  }, []);

  const checkSpotifyConnection = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/spotify/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSpotifyProfile(response.data);
      setIsPremium(response.data.is_premium);
      setIsConnected(response.data.connected);
      
      // Get fresh access token from user data
      const userResponse = await axios.get('/api/dashboard', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIsLoading(false);
    } catch (error) {
      console.log('Spotify not connected yet');
      setIsConnected(false);
      setIsLoading(false);
    }
  };

  const handleSpotifyLogin = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/spotify/login', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open Spotify OAuth in new window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const authWindow = window.open(
        response.data.auth_url,
        'Spotify Login',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Listen for callback
      const checkAuth = setInterval(() => {
        try {
          if (authWindow.closed) {
            clearInterval(checkAuth);
            // Recheck connection after window closes
            setTimeout(() => {
              checkSpotifyConnection();
              toast.success('Spotify connected successfully!');
            }, 1000);
          }
        } catch (e) {
          // Cross-origin errors are expected
        }
      }, 500);
    } catch (error) {
      console.error('Spotify login error:', error);
      toast.error('Failed to connect to Spotify');
    }
  };

  const handleDisconnect = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/spotify/disconnect', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIsConnected(false);
      setSpotifyProfile(null);
      setIsPremium(false);
      
      if (player) {
        player.disconnect();
        setPlayer(null);
        setDeviceId(null);
      }
      
      toast.success('Spotify disconnected');
    } catch (error) {
      console.error('Disconnect error:', error);
      toast.error('Failed to disconnect Spotify');
    }
  };

  // Initialize Spotify Web Playback SDK for Premium users
  useEffect(() => {
    if (!isConnected || !isPremium || !accessToken) return;

    const script = document.createElement('script');
    script.src = 'https://sdk.scdn.co/spotify-player.js';
    script.async = true;
    document.body.appendChild(script);

    window.onSpotifyWebPlaybackSDKReady = () => {
      const spotifyPlayer = new window.Spotify.Player({
        name: 'The Brain Superheroes Wellness App',
        getOAuthToken: cb => { cb(accessToken); },
        volume: 0.5
      });

      // Ready
      spotifyPlayer.addListener('ready', ({ device_id }) => {
        console.log('Spotify Player Ready with Device ID', device_id);
        setDeviceId(device_id);
        onReady && onReady(device_id);
      });

      // Not Ready
      spotifyPlayer.addListener('not_ready', ({ device_id }) => {
        console.log('Device ID has gone offline', device_id);
      });

      // Player state changed
      spotifyPlayer.addListener('player_state_changed', (state) => {
        if (!state) return;
        onPlayerStateChanged && onPlayerStateChanged(state);
      });

      // Connect to the player
      spotifyPlayer.connect().then(success => {
        if (success) {
          console.log('Spotify Player connected successfully');
        }
      });

      setPlayer(spotifyPlayer);
    };

    return () => {
      if (player) {
        player.disconnect();
      }
    };
  }, [isConnected, isPremium, accessToken]);

  if (isLoading) {
    return (
      <Card className="glass-card p-4">
        <div className="text-center text-gray-600">
          🎵 Loading Spotify connection...
        </div>
      </Card>
    );
  }

  if (!isConnected) {
    return (
      <Card className="glass-card p-6">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center">
            <span className="text-3xl">🎵</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Connect to Spotify
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Access your Spotify music for calming meditation and relaxation sessions
          </p>
          <Button 
            onClick={handleSpotifyLogin}
            className="btn-primary"
          >
            Connect Spotify Account
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="glass-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center">
            <span className="text-xl">🎵</span>
          </div>
          <div>
            <h3 className="font-medium text-gray-900 text-sm">
              Spotify Connected
            </h3>
            <p className="text-xs text-gray-500">
              {spotifyProfile?.display_name}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {isPremium && (
            <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs">
              Premium
            </Badge>
          )}
          <Button
            onClick={handleDisconnect}
            variant="outline"
            size="sm"
            className="text-xs"
          >
            Disconnect
          </Button>
        </div>
      </div>

      {!isPremium && (
        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-700">
            ℹ️ Web playback requires Spotify Premium. You can still search and open tracks in Spotify.
          </p>
        </div>
      )}

      {isPremium && deviceId && (
        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-700">
            ✅ Spotify player ready! Search for calming music below.
          </p>
        </div>
      )}
    </Card>
  );
};

export default SpotifyPlayer;
